import React, { useEffect, useMemo, useState } from "react";
import { Paper, Stack, Grid2 as Grid, Container } from "@mui/material";
import PageHeader from "@/components/PageHeader";
import CustomerPhoneSearchField from "@/components/CustomerPhoneSearchField";
import { useLazyGetCustomerByPhoneQuery, orgCustomersApi } from "@/features/organizations/api/orgCustomersApi";
import { useAppSelector, useAppDispatch } from "@/hooks";
import {
  useLazyGetLoyaltyAccountQuery,
  useLazyFetchRedeemableContentQuery,
  useLazyGetLoyaltyTransactionsQuery,
  loyaltyApi
} from "@/api/loyaltyApi";
import LoyaltyAccountSummary from "@/components/loyalty/LoyaltyAccountSummary";
import LoyaltyClientProfile from "@/components/loyalty/LoyaltyClientProfile";
import LoyaltyAvailableRewards from "@/components/loyalty/LoyaltyAvailableRewards";
import LoyaltyTransactionsList from "@/components/loyalty/LoyaltyTransactionsList";
import RedemptionDialog from "@/components/loyalty/RedemptionDialog";
import { Content } from "@/types";

const LoyaltyPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const container = useAppSelector((state) => state.ui?.container);
  const [phone, setPhone] = useState("");
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);
  const [searchUserByPhone, userByPhoneMutation] = useLazyGetCustomerByPhoneQuery();
  const [getLoyaltyAccount, loyaltyAccountQuery] = useLazyGetLoyaltyAccountQuery();
  const [fetchRedeemableContent, redeemableContentQuery] = useLazyFetchRedeemableContentQuery();
  const [getLoyaltyTransactions, loyaltyTransactionsQuery] = useLazyGetLoyaltyTransactionsQuery();
  const [selectedReward, setSelectedReward] = useState<Content | null>(null);

  const handleRedeem = (reward: Content) => {
    setSelectedReward(reward);
  };

  const handleSearch = () => {
    if (userByPhoneMutation.isLoading || phone.length < 6) {
      return;
    }
    searchUserByPhone({ organizationId, phone });
  };

  const handleClear = () => {
    setPhone("");
    dispatch(orgCustomersApi.util.resetApiState());
    dispatch(loyaltyApi.util.resetApiState());
  }

  const firstUserFound = useMemo(() => {
    const usersFound = userByPhoneMutation.data;
    const results = Array.isArray(usersFound) ? usersFound : usersFound ? [usersFound] : [];
    return results[0] as any;
  }, [userByPhoneMutation.data])

  useEffect(() => {
    if (firstUserFound?.id && organizationId) {
      getLoyaltyAccount({ organizationId, uid: firstUserFound.id });
    }
  }, [firstUserFound, organizationId, getLoyaltyAccount]);

  useEffect(() => {
    if (loyaltyAccountQuery.isSuccess && loyaltyAccountQuery.data) {
      fetchRedeemableContent({ organizationId });
      getLoyaltyTransactions({ organizationId, uid: loyaltyAccountQuery.data.uid });
    }
  }, [loyaltyAccountQuery.isSuccess, loyaltyAccountQuery.data, organizationId, fetchRedeemableContent, getLoyaltyTransactions]);

  return (
    <Stack spacing={3}>
      <PageHeader title="Programa de Fidelización" />

      <Stack>
        <Container
          maxWidth={container === 'fixed' ? 'lg' : false}
          sx={{
            height: '100%',
          }}
        >

          <Stack spacing={5}>
            <Paper sx={{ p: 3 }}>
              <CustomerPhoneSearchField
                label=""
                fullWidth
                size="large"
                value={phone}
                onClear={handleClear}
                onSearch={handleSearch}
                onChange={(val) => setPhone(val)}
                isLoading={userByPhoneMutation.isLoading || loyaltyAccountQuery.isLoading}
              />
            </Paper>

            <Grid container columnSpacing={3} spacing={3} sx={{ flexDirection: { lg: "row-reverse" } }}>
              <Grid
                size={{
                  xs: 12,
                  lg: 8
                }}
              >
                <Stack spacing={3} paddingBottom={4}>
                  {!loyaltyAccountQuery.isUninitialized ? (
                    <LoyaltyAccountSummary
                      isLoading={loyaltyAccountQuery.isLoading}
                      isSuccess={loyaltyAccountQuery.isSuccess}
                      currentPoints={loyaltyAccountQuery.data?.currentPoints ?? 0}
                      totalRedeemed={loyaltyAccountQuery.data?.totalRedeemed ?? 0}
                      totalEarned={loyaltyAccountQuery.data?.totalEarned ?? 0}
                    />
                  ) : null}
                  {!redeemableContentQuery.isUninitialized ? (
                    <LoyaltyAvailableRewards
                      isLoading={redeemableContentQuery.isLoading}
                      isSuccess={redeemableContentQuery.isSuccess}
                      rewards={redeemableContentQuery.data || []}
                      currentPoints={loyaltyAccountQuery.data?.currentPoints ?? 0}
                      onRedeem={handleRedeem}
                    />
                  ) : null}
                  {!loyaltyTransactionsQuery.isUninitialized ? (
                    <LoyaltyTransactionsList
                      isLoading={loyaltyTransactionsQuery.isLoading}
                      isSuccess={loyaltyTransactionsQuery.isSuccess}
                      transactions={loyaltyTransactionsQuery.data || []}
                    />
                  ) : null}
                </Stack>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 4
                }}
                rowGap={3}
                rowSpacing={3}
              >
                {!userByPhoneMutation.isUninitialized ? (
                  <LoyaltyClientProfile
                    client={firstUserFound}
                    isLoading={userByPhoneMutation.isLoading}
                    isSuccess={userByPhoneMutation.isSuccess}
                    loyaltyAccount={loyaltyAccountQuery.data}
                  />
                ) : null}
              </Grid>
            </Grid>
          </Stack>

          <RedemptionDialog
            open={Boolean(selectedReward)}
            onClose={() => setSelectedReward(null)}
            reward={selectedReward}
            loyaltyAccount={loyaltyAccountQuery.data || null}
            client={firstUserFound}
          />
        </Container>
      </Stack>
    </Stack>
  );
};

export default LoyaltyPage;
